// Pure helpers for the API security page: endpoint shape, issue groups from audit findings, and request bodies.

// The audit tags each finding with raw_data.check. Plain wording for each one.
export const CHECKS = {
  bola: { title: "Users can read other users' data", why: "Changing the ID in the URL returned a record that did not belong to the caller.", fix: 'Check that the record belongs to the signed-in user on every request.', rank: 0 },
  auth_bypass: { title: 'Endpoints that need no sign-in', why: 'These answered without credentials but look like they should be protected.', fix: 'Require authentication, or document the endpoint as public on purpose.', rank: 1 },
  rate_limit: { title: 'No rate limit', why: 'A burst of requests was not slowed down or blocked.', fix: 'Add a limit per user and per IP, at least on login and reset endpoints.', rank: 2 },
  schema_drift: { title: 'Responses differ from the spec', why: 'Fields or status codes do not match the OpenAPI file.', fix: 'Update the spec or fix the response so they agree.', rank: 3 }
};
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

export const normalizeEndpoint = (ep) => ({
  id: ep.id, method: String(ep.method || 'GET').toUpperCase(), path: ep.path || '',
  auth: ep.auth || ep.auth_type || 'unknown', rateLimit: typeof ep.rateLimit === 'boolean' ? ep.rateLimit : Boolean(ep.rate_limited),
  lastTested: ep.last_tested_at || null
});
export const endpointKey = (method, path) => `${String(method || 'GET').toUpperCase()} ${path}`;

// The newest finished audit decides what is shown, so repeated audits do not pile up.
export function latestAudit(scans) {
  return [...scans].filter((s) => s.scan_type === 'api_active_security' && ['completed', 'finished'].includes(s.status))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;
}

// -> groups [{ check, title, why, fix, severity, items: [{ method, path, finding }] }], worst first.
export function groupIssues(findings) {
  const map = new Map();
  findings.forEach((f) => {
    const raw = f.raw_data || {};
    const check = raw.check || `other:${f.title}`;
    const info = CHECKS[raw.check] || { title: f.title, why: String(f.description || '').split('\n')[0], fix: '', rank: 9 };
    const g = map.get(check) || { check, ...info, severity: 'info', items: [] };
    if ((SEV_RANK[f.severity] ?? 9) < (SEV_RANK[g.severity] ?? 9)) g.severity = f.severity;
    g.items.push({ method: String(raw.method || 'GET').toUpperCase(), path: raw.endpoint || '', finding: f });
    map.set(check, g);
  });
  return [...map.values()].sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || a.rank - b.rank);
}

// "METHOD /path" -> findings from the shown audit.
export function issuesByEndpoint(findings) {
  const m = {};
  findings.forEach((f) => { const k = endpointKey(f.raw_data?.method, f.raw_data?.endpoint); (m[k] = m[k] || []).push(f); });
  return m;
}

export function auditBody({ userTargetId, baseUrl, timeout, burst, authHeader, authValue }) {
  return {
    user_target: userTargetId, base_url: String(baseUrl || '').trim(), timeout: Number(timeout || 10), rate_limit_burst: Number(burst || 8),
    auth_header: String(authHeader || '').trim(), auth_value: String(authValue || '').trim()
  };
}
